import { Container, Graphics, interaction, Point } from 'pixi.js';

import { Game } from '../core';

/**
 * Scroll state
 */
enum ScrollState {
  Rest,
  Drag,
  Slide,
  Out
}

/**
 * scrollWidth: width of scroll display area
 * scrollHeight: height of scroll display area
 * backgroundAlpha: background alpha of scroll display area
 * backgroundColor: background color of scroll display area
 * paddingTop: padding between content top and display area top
 * paddingBottom: padding between content bottom and display area bottom
 */
type ScrollOptions = {
  scrollWidth: number;
  scrollHeight: number;
  backgroundAlpha: number;
  backgroundColor: number;
  paddingTop: number;
  paddingBottom: number;
};

const defaultOptions = {
  scrollWidth: 360,
  scrollHeight: 640,
  backgroundAlpha: 0.5,
  backgroundColor: 0x00000,
  paddingTop: 0,
  paddingBottom: 0
};

export class ScrollContainer extends Container {
  private readonly THRESHOLD_MOVE_DIST: number = 20;
  private readonly ROUNDABLE_VELOCITY: number = 0.1;
  private readonly INERTIA: number = 0.965;
  private readonly ELASTICITY: number = 0.86;
  private readonly MAX_VELOCITY: number = 50;

  private readonly bg: Container; // background graphics
  private readonly content: Container; // scroll element container
  private readonly scrollHeight: number; // height of scroll display area
  private readonly borderMax: number; // max y value (top)
  private readonly borderMin: number; // min y value (bottom)

  private state: ScrollState = ScrollState.Rest;
  private prePos: number = 0;
  private velocity: number = 0;
  private touchStartPos: number;
  private touchStartPointer: { x: number; y: number };

  /**
   * Constructor
   * @param content Scroll element container
   * @param scrollWidth Scroll display width
   * @param scrollHeight Scroll display height
   * @param padding Padding between element and display space
   */
  public constructor(content: Container, options: Partial<ScrollOptions>) {
    super();
    this.interactive = true;
    this.content = content;
    this.prePos = this.content.y;

    const mergedOptions = { ...defaultOptions, ...options };
    this.scrollHeight = mergedOptions.scrollHeight;
    this.borderMax = mergedOptions.paddingTop;
    this.borderMin = Math.min(-this.content.getBounds().bottom + this.scrollHeight - mergedOptions.paddingBottom, 0);

    this.on('mousedown', (event: interaction.InteractionEvent) => this.startScroll(event));
    this.on('mousemove', (event: interaction.InteractionEvent) => this.scroll(event));
    this.on('mouseup', (_: interaction.InteractionEvent) => this.finishScroll());
    this.ecUpdate = (_: number) => {
      this.update();
    };

    const bg = new Graphics();
    bg.name = 'ScrollBackground';
    bg.interactive = true;
    bg.beginFill(mergedOptions.backgroundColor, mergedOptions.backgroundAlpha);
    bg.drawRoundedRect(0, 0, mergedOptions.scrollWidth, mergedOptions.scrollHeight, 0);
    bg.endFill();
    this.addChild(bg);
    this.bg = bg;

    const mask = new Graphics();
    mask.name = 'ScrollMask';
    mask.beginFill(0x000000, 1);
    mask.drawRoundedRect(0, 0, mergedOptions.scrollWidth, mergedOptions.scrollHeight, 0);
    mask.endFill();
    this.addChild(mask);
    this.mask = mask;

    this.addChild(content);
  }

  private update(): void {
    // if element is out of scroll container, make visible false
    for (const child of this.content.children) {
      const height = child.getBounds().height;
      const isTopOver = -this.content.y > child.y + height * 0.5;
      const isBottomOver = -this.content.y + this.scrollHeight < child.y - height * 0.5;
      child.visible = !isTopOver && !isBottomOver;
    }

    if (this.state === ScrollState.Drag) return;

    // update velocity and move
    this.velocity = this.velocity * this.INERTIA;
    if (Math.abs(this.velocity) < this.ROUNDABLE_VELOCITY) {
      this.velocity = 0;
    }
    this.move(this.content.y + this.velocity);

    // update state
    if (this.velocity === 0) {
      if (this.content.y < this.borderMin || this.borderMax < this.content.y) {
        this.state = ScrollState.Out;
      } else {
        this.state = ScrollState.Rest;
        this.content.y = Math.trunc(this.content.y * 10) * 0.1;
      }
    }
  }

  private startScroll(e: interaction.InteractionEvent): void {
    if (this.isInnerScroll(e.data.global)) {
      this.state = ScrollState.Drag;
      this.touchStartPointer = { x: e.data.global.x, y: e.data.global.y };
      this.touchStartPos = this.content.y;
    }
  }

  private isInnerScroll(p: Point): boolean {
    const isInnerBg = this.bg.getBounds().contains(p.x, p.y);
    const isInnerScreen = 0 <= p.x && p.x <= Game.width && 0 <= p.y && p.y <= Game.height;
    return isInnerBg && isInnerScreen;
  }

  private scroll(e: interaction.InteractionEvent): void {
    if (this.state !== ScrollState.Drag) return;

    // mouse move to out of scroll container
    if (!this.isInnerScroll(e.data.global)) {
      this.state = ScrollState.Slide;
      return;
    }

    // whether mouse moving range is valid
    const delta = this.calcDelta(e);
    if (this.THRESHOLD_MOVE_DIST < delta && delta < this.scrollHeight) {
      const pointerDeltaY = e.data.global.y - this.touchStartPointer.y;
      const y = this.touchStartPos + pointerDeltaY;
      this.velocity = Math.max(Math.min(y - this.prePos, this.MAX_VELOCITY), -this.MAX_VELOCITY);
      this.prePos = y;
      this.move(y);
    }
  }

  private finishScroll(): void {
    if (this.state === ScrollState.Drag) {
      this.state = ScrollState.Slide;
    }
  }

  private calcDelta(e: interaction.InteractionEvent): number {
    const pos = e.data.global;
    const deltaX = this.touchStartPointer.x - pos.x;
    const deltaY = this.touchStartPointer.y - pos.y;
    return Math.sqrt(deltaX * deltaX + deltaY * deltaY);
  }

  private move(y: number): void {
    const pos = this.content.position;
    if (y > this.borderMax) {
      // over top wall
      this.velocity *= this.ELASTICITY;
      pos.y = this.borderMax + (y - this.borderMax) * this.ELASTICITY;
      if (Math.abs(pos.y - this.borderMax) < 1) pos.y = this.borderMax;
    } else if (y < this.borderMin) {
      // under bottom wall
      this.velocity *= this.ELASTICITY;
      pos.y = this.borderMin + (y - this.borderMin) * this.ELASTICITY;
      if (Math.abs(pos.y - this.borderMin) < 1) pos.y = this.borderMin;
    } else {
      pos.y = y;
    }
  }
}
